import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  Trophy, Target, Flame, TrendingUp, TrendingDown, Loader2, Play, ArrowRight, Zap, ThumbsUp, ThumbsDown,
} from "lucide-react";

const chartAxis = { fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: "hsl(var(--muted-foreground))" };
const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 };

function heatColor(pct, attempts) {
  if (!attempts) return "bg-secondary/40 border-border";
  if (pct >= 85) return "bg-accent/60 border-accent/50";
  if (pct >= 70) return "bg-primary/50 border-primary/50";
  if (pct >= 50) return "bg-primary/25 border-primary/30";
  return "bg-destructive/40 border-destructive/40";
}

function shortDay(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.getUTCDate();
}

export default function QuizStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rated, setRated] = useState(null); // 'up' | 'down' | null

  const sendRating = async (kind) => {
    if (rated) return;
    setRated(kind);
    try {
      await api.post("/feedback", {
        kind: "general",
        message: `Weakest-topic ranking felt ${kind === "up" ? "spot on" : "off"}`,
        rating: kind === "up" ? 5 : 1,
        page: window.location.pathname,
      });
      toast.success(kind === "up" ? "Thanks — noted." : "Got it — we'll tune it.");
    } catch {
      toast.error("Could not send — try again?");
      setRated(null);
    }
  };

  useEffect(() => {
    api.get("/me/quiz-stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-24 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <div className="mt-3 label-mono">Crunching your quiz history…</div>
      </div>
    );
  }
  if (!stats) return <div className="p-6 text-sm text-muted-foreground">Could not load stats.</div>;

  const t = stats.totals || {};
  const hasData = (t.attempts || 0) > 0;

  return (
    <div data-testid="quiz-stats-root" className="space-y-8 animate-fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-primary">// quiz stats</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Your quiz mastery.</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Every quiz you take feeds this page. See where you&apos;re crushing it, where you need reps, and how you&apos;ve grown over the last 30 days.
          </p>
        </div>
        <Link
          to="/app/paths"
          data-testid="quiz-stats-practice-cta"
          className="rounded-full btn-neon inline-flex items-center gap-2 px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-primary-foreground"
        >
          <Play className="h-4 w-4" /> Practice unlimited
        </Link>
      </div>

      {!hasData ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <Target className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-3 font-display text-lg font-bold">No quiz history yet.</div>
          <div className="mt-1 text-sm text-muted-foreground">Take your first lesson quiz to unlock personalised stats.</div>
          <Link to="/app/paths" className="mt-4 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-primary hover:underline">
            Browse paths <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <>
          {/* Top metrics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div data-testid="stats-metric-accuracy" className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-5">
              <div className="flex items-center justify-between">
                <div className="label-mono">Overall accuracy</div>
                <Target className="h-4 w-4 text-primary" />
              </div>
              <div className="mt-3 font-display text-4xl font-black tracking-tighter">{t.overall_accuracy}%</div>
              <div className="label-mono">{t.correct} / {t.questions_answered} correct</div>
            </div>
            <div data-testid="stats-metric-attempts" className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="label-mono">Quizzes taken</div>
                <Zap className="h-4 w-4 text-primary" />
              </div>
              <div className="mt-3 font-display text-4xl font-black tracking-tighter">{t.attempts}</div>
              <div className="label-mono">across {(stats.per_path || []).length} path{(stats.per_path || []).length !== 1 ? "s" : ""}</div>
            </div>
            <div data-testid="stats-metric-streak" className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="label-mono">Perfect streak</div>
                <Flame className="h-4 w-4 text-accent" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-4xl font-black tracking-tighter">{t.current_streak}</div>
                <div className="text-sm text-muted-foreground">in a row</div>
              </div>
              <div className="label-mono">consecutive 100% scores</div>
            </div>
            <div data-testid="stats-metric-best" className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="label-mono">Best path</div>
                <Trophy className="h-4 w-4 text-accent" />
              </div>
              {(stats.per_path || []).length > 0 ? (
                <>
                  <div className="mt-3 font-display text-2xl font-black tracking-tighter truncate">
                    {[...(stats.per_path || [])].sort((a, b) => b.avg_percent - a.avg_percent)[0].path_title}
                  </div>
                  <div className="label-mono">{[...(stats.per_path || [])].sort((a, b) => b.avg_percent - a.avg_percent)[0].avg_percent}% avg</div>
                </>
              ) : <div className="mt-3 label-mono">no paths yet</div>}
            </div>
          </div>

          {/* Per-path bar chart */}
          <div data-testid="stats-per-path" className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <h2 className="font-display text-xl font-bold">Accuracy by learning path</h2>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.per_path || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="path_title" tick={chartAxis} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={chartAxis} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="avg_percent" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Weakest + strongest topics */}
          <div className="grid gap-4 md:grid-cols-2">
            <div data-testid="stats-weakest" className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-destructive" />
                  <h2 className="font-display text-lg font-bold">Weakest topics</h2>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-mono text-[0.6rem] uppercase tracking-widest text-muted-foreground mr-1 hidden sm:inline">feels right?</span>
                  <button
                    data-testid="stats-weakest-thumbsup"
                    onClick={() => sendRating("up")}
                    disabled={!!rated}
                    title="This ranking looks correct"
                    className={`rounded-full border p-1.5 transition ${rated === "up" ? "border-accent bg-accent/20 text-accent" : "border-border hover:border-accent/60 text-muted-foreground hover:text-accent"} ${rated ? "cursor-default" : ""}`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid="stats-weakest-thumbsdown"
                    onClick={() => sendRating("down")}
                    disabled={!!rated}
                    title="This ranking feels off"
                    className={`rounded-full border p-1.5 transition ${rated === "down" ? "border-destructive bg-destructive/20 text-destructive" : "border-border hover:border-destructive/60 text-muted-foreground hover:text-destructive"} ${rated ? "cursor-default" : ""}`}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {(stats.weakest_topics || []).length ? (
                <ul className="space-y-2">
                  {(stats.weakest_topics || []).map((w, i) => (
                    <li key={i} className="flex items-center justify-between rounded-lg border border-border bg-background/60 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{w.lesson_title}</div>
                        <div className="label-mono truncate">{w.path_title} · {w.attempts} attempt{w.attempts !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="flex-shrink-0 rounded-full border border-destructive/50 bg-destructive/10 px-3 py-1 font-mono text-xs text-destructive">
                        {w.avg_percent}%
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <div className="label-mono">no weak topics yet — great!</div>}
            </div>

            <div data-testid="stats-strongest" className="rounded-2xl border border-accent/40 bg-accent/5 p-6">
              <div className="mb-3 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-accent" />
                <h2 className="font-display text-lg font-bold">Strongest topics</h2>
              </div>
              {(stats.strongest_topics || []).length ? (
                <ul className="space-y-2">
                  {(stats.strongest_topics || []).map((s, i) => (
                    <li key={i} className="flex items-center justify-between rounded-lg border border-border bg-background/60 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.lesson_title}</div>
                        <div className="label-mono truncate">{s.path_title} · {s.attempts} attempt{s.attempts !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="flex-shrink-0 rounded-full border border-accent/50 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
                        {s.avg_percent}%
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <div className="label-mono">complete a few quizzes to see your best areas</div>}
            </div>
          </div>

          {/* 30-day heatmap */}
          <div data-testid="stats-heatmap" className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-2 flex items-center gap-2">
              <Flame className="h-4 w-4 text-primary" />
              <h2 className="font-display text-xl font-bold">Last 30 days</h2>
            </div>
            <p className="mb-4 label-mono">
              Colour = average quiz accuracy that day. Empty = rest day.
            </p>
            <div className="grid grid-cols-10 gap-1.5 sm:grid-cols-15 md:grid-cols-30">
              {(stats.heatmap_30d || []).map((d) => (
                <div
                  key={d.date}
                  title={`${d.date} · ${d.attempts} quiz${d.attempts !== 1 ? "es" : ""}${d.avg_percent != null ? ` · ${d.avg_percent}%` : ""}`}
                  className={`aspect-square rounded-md border ${heatColor(d.avg_percent, d.attempts)} flex items-center justify-center`}
                >
                  <span className="font-mono text-[0.55rem] text-muted-foreground">{shortDay(d.date)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3 label-mono">
              <span>less</span>
              <div className="h-2.5 w-4 rounded bg-secondary/40 border border-border" />
              <div className="h-2.5 w-4 rounded bg-destructive/40 border border-destructive/40" />
              <div className="h-2.5 w-4 rounded bg-primary/25 border border-primary/30" />
              <div className="h-2.5 w-4 rounded bg-primary/50 border border-primary/50" />
              <div className="h-2.5 w-4 rounded bg-accent/60 border border-accent/50" />
              <span>more</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
